<template>
  <div class="accordion-item">
    <div class="settings">
      <div class="model">
        <Multiselect
          v-model="localItem.model"
          label="Model"
          :searchable="false"
          placeholder="Select a model (optional)"
          :options="modelOptions"
          :disabled="isBusy"
          :get-label-callback="id => getLabel(id)"
          @input="onChange"
        />
        <div><span v-if="!localItem.model">*organization chat model will be used</span></div>
        <div>
          <span v-if="!isModelAllowed(localItem.model)" class="error">Not all organizations this template is assigned for allow selected model</span>
        </div>
      </div>
      <ul class="params">
        <li><TextField v-model="localItem.temperature" :disabled="isBusy" label="Temperature" type="number" @input="onChange" /></li>
        <li><TextField v-model="localItem.max_tokens" :disabled="isBusy" label="Max tokens" type="number" @input="onChange" /></li>
        <li v-for="(param, index) in localItem.params" :key="index" class="param">
          <Multiselect v-if="param.options" v-model="param.value" :label="param.name" :options="param.options" :disabled="isBusy" @input="onChange" />
          <TextField v-else v-model="param.value" :label="param.name" :disabled="isBusy" @input="onChange" />
        </li>
      </ul>
      <div v-if="getTools(localItem.model)" class="functions">
        <Button color="primary" title="Functions" @click="toggleFunctions">
          <div>
            <MdIcon size="sm" name="function-variant" />
            {{ getTools(localItem.model).filter(f => f.selected).length }} attached
            <MdIcon size="sm" :name="functionsExpanded ? 'menu-up' : 'menu-down'" />
          </div>
        </Button>
      </div>
      <div v-if="functionsExpanded" :class="{ expanded: functionsExpanded }" class="functions">
        <div v-if="!isAllToolsAllowedByOrganizations()">
          <span class="error">Not all tools are allowed by the organizations assigned to this chain.</span>
        </div>
        <ul class="list">
          <li v-for="func in getTools(localItem.model)" :key="func.name" class="function">
            <Checkbox v-if="func.isAllowed" v-model="func.selected" @change="s => functionChecked(func.name, s)" />
            <span v-else />
            <span :class="{ 'not-allowed': !func.isAllowed }" :title="!func.isAllowed ? 'This tool is not allowed for this model' : ''">
              <label> {{ func.name }} </label>({{ !!func.userDescritpion ? func.userDescritpion : func.description }})
            </span>
          </li>
        </ul>
      </div>
      <div class="skills-selects">
        <Multiselect
          v-model="localItem.skills"
          :options="skillOptions"
          :get-label-callback="getSkillLabel"
          :disabled="isBusy"
          label="Allowed Skills"
          placeholder="Select allowed skills (optional)"
          :searchable="true"
          :multiple="true"
          @input="onChange"
        />
        <Multiselect
          v-model="localItem.activeSkills"
          :options="skillOptions"
          :get-label-callback="getSkillLabel"
          :disabled="isBusy"
          label="Active Skills"
          placeholder="Select active skills (optional)"
          :searchable="true"
          :multiple="true"
          @input="onChange"
        />
        <div v-if="hasSkillOverlap" class="error">Warning: some skills appear in both Allowed and Active lists.</div>
      </div>
    </div>
    <div class="prompt-body-content">
      <div v-for="(message, messageIndex) in localItem.messages" :key="messageIndex" class="template-content">
        <div class="head">
          <span class="label">{{ message.role }}:</span>
          <Button v-if="messageIndex === localItem.messages.length - 1 && localItem.messages.length > 2" color="" @click="deleteMessage">
            <MdIcon size="sm" name="delete" />
          </Button>
        </div>
        <Code
          v-model="message.content"
          :read-only="!$hasPermission('prompts.write') || isBusy"
          :language="'plaintext'"
          :suggestions="suggestions"
          class="editor"
          :auto-height="true"
          @input="onChange"
        />
        <ImageVariable v-if="hasImageAttachments(message)" v-model="message.supplementals.images" label="Image examples" />
      </div>
      <div class="buttons">
        <Button color="primary" :disabled="isBusy" @click="addMessage">+ Message</Button>
        <div v-if="imageVariables && imageVariables.length" class="attachments">
          <Button color="primary" title="Attached variables" :disabled="isBusy" @click="toggleAttach">
            <div>
              <MdIcon size="sm" name="paperclip" class="attachments" />
              <MdIcon size="sm" :name="attachmentsExpanded ? 'menu-up' : 'menu-down'" class="attachments" />
            </div>
          </Button>
          <div v-if="attachmentsExpanded" class="attachemnts-pop-up" :class="{ expanded: attachmentsExpanded }">
            <ul>
              <li>Images:</li>
              <li v-for="attachment in imageVariables" :key="attachment.name" class="attachment">
                <Checkbox v-model="attachment.selected" @change="s => attachmentChecked(attachment.name, s)" />
                <label>{{ attachment.name || attachment.label }}</label>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import Code from '@/components/common/Code';
import Button from '@/components/common/Button';
import Multiselect from '@/components/common/Multiselect';
import TextField from '@/components/common/TextField';
import MdIcon from '@/components/common/MdIcon';
import Checkbox from '@/components/common/Checkbox';
import ImageVariable from '../variables/ImageVariable';

export default {
  components: {
    ImageVariable,
    Code,
    Multiselect,
    TextField,
    Button,
    MdIcon,
    Checkbox
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    template: {
      type: Object,
      required: true
    },
    suggestions: {
      type: Array,
      required: true
    },
    variables: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localItem: {
        ...this.item,
        messages: this.item.messages.map(message => ({
          ...message,
          supplementals: {
            ...(message.supplementals ?? { images: [] })
          }
        })),
        attachments: this.item.attachments ?? [],
        functions: this.item.functions ?? [],
        skills: this.item.skills ?? [],
        activeSkills: this.item.activeSkills ?? [],
        params: this.item.params ?? []
      },
      expanded: false,
      attachmentsExpanded: false,
      functionsExpanded: false,
      editingTitle: false
    };
  },
  computed: {
    ...mapState({
      modelPermissions: s => s.nlp.modelPermissions || [],
      providers: state => state.nlp.providers || [],
      active: s => !s.prompts.sample.isRequestPending,
      tools: state => state.nlp.tools ?? [],
      modelPermissions: state => state.nlp.modelPermissions ?? [],
      skills: state => state.nlp.skills ?? []
    }),
    ...mapGetters({
      models: 'nlp/chatModels',
      isBusy: 'prompts/isBusy'
    }),
    selectedModel() {
      return this.models.find(m => m.id === this.localItem.model);
    },
    isVisionModel() {
      return this.selectedModel?.capability.endsWith('vision') ?? false;
    },
    imageVariables() {
      return this.variables
        .filter(v => v.type === 'image')
        .filter(v => v.name && v.name.length)
        .map(v => ({
          name: v.name,
          selected: this.localItem.attachments.includes(v.name)
        }));
    },
    modelOptions() {
      return ['', ...this.models.map(m => m.id)];
    },
    skillOptions() {
      return this.skills.map(s => s.name);
    },
    hasSkillOverlap() {
      const allowed = this.localItem.skills ?? [];
      const preloaded = this.localItem.activeSkills ?? [];
      return allowed.some(s => preloaded.includes(s));
    },
    allowedModels() {
      const organizations = this.template?.organizations ?? [];

      if (!organizations.length) {
        return [''];
      }

      const allowedModels = [];

      const organizationsPermisssions = this.modelPermissions.filter(mp => organizations.includes(mp.organization));

      for (const model of new Set(organizationsPermisssions.map(mp => mp.model))) {
        if (organizationsPermisssions.filter(op => op.model === model).length === organizations.length) {
          allowedModels.push(model);
        }
      }

      return ['', ...allowedModels];
    }
  },
  watch: {
    skillOptions(newOptions) {
      if (!newOptions.length) return;
      const valid = new Set(newOptions);
      const removedFromAllowed = (this.localItem.skills ?? []).filter(s => !valid.has(s));
      const removedFromPreloaded = (this.localItem.activeSkills ?? []).filter(s => !valid.has(s));
      const removed = [...new Set([...removedFromAllowed, ...removedFromPreloaded])];
      if (!removed.length) return;
      this.localItem.skills = (this.localItem.skills ?? []).filter(s => valid.has(s));
      this.localItem.activeSkills = (this.localItem.activeSkills ?? []).filter(s => valid.has(s));
      this.$toast.warning({
        title: 'Skill removed',
        message: `The following skill(s) no longer exist and were removed: ${removed.join(', ')}. Save the chain to apply.`
      });
      this.onChange();
    },
    'localItem.model': {
      handler() {
        const functions = this.getTools(this.localItem.model);
        if (!functions) {
          this.functionsExpanded = false;
          this.localItem.functions = [];
        }
        const params = this.selectedModel?.params ?? [];

        if (params) {
          this.localItem.params = params.map(p => ({
            name: p.name,
            value: p.default ?? '',
            options: p.options
          }));
        } else {
          this.localItem.params = [];
        }

        this.onChange();
      }
    }
  },
  created() {
    this.localItem.model = this.modelBackwardCompatibility(this.localItem.model);
    this.$store.dispatch('nlp/getSkills');
  },
  methods: {
    getSkillLabel(name) {
      const skill = this.skills.find(s => s.name === name);
      return skill ? `${name} — ${skill.displayDescription}` : name;
    },
    isAllToolsAllowedByOrganizations() {
      const organizations = this.template?.organizations ?? [];

      if (!organizations.length) {
        return true;
      }

      const allowedTools = this.getTools(this.localItem.model) ?? [];
      for (const tool of allowedTools.filter(t => t.organizations?.length)) {
        if (!organizations.every(org => tool.organizations?.includes(org))) {
          return false;
        }
      }

      return true;
    },
    isModelAllowed(model) {
      return !model || this.allowedModels.includes(model);
    },
    hasImageAttachments(message) {
      if (!this.isVisionModel || message.role !== 'user') {
        return false;
      }

      return this.localItem.messages.indexOf(message) !== this.localItem.messages.length - 1;
    },
    toggleFunctions() {
      this.functionsExpanded = !this.functionsExpanded;
    },
    isToolAllowed(toolName, modelId) {
      const tool = this.tools.find(t => t.name === toolName);
      if (!tool) {
        return false;
      }

      if (!tool.models?.length) {
        return true;
      }

      const provider = this.providers.find(p => p.name === this.selectedModel?.provider);

      if (!provider?.toolsSupported) {
        return false;
      }

      return tool.models.includes(modelId);
    },
    getTools(modelId) {
      if (!modelId) {
        return undefined;
      }

      const model = this.models.find(m => m.id == modelId);

      if (!model) {
        return undefined;
      }

      const provider = this.providers?.find(p => p.name === model.provider);

      if (provider?.toolsSupported) {
        return this.tools.map(f => ({
          ...f,
          isAllowed: this.isToolAllowed(f.name, modelId),
          selected: this.localItem.functions?.includes(f.name) ?? false
        }));
      }

      return undefined;
    },
    functionChecked(name, checked) {
      if (checked) {
        this.localItem.functions.push(name);
      } else {
        var index = this.localItem.functions.indexOf(name);
        this.localItem.functions.splice(index, 1);
      }

      this.$emit('change', { ...this.localItem });
    },
    attachmentChecked(name, checked) {
      if (checked) {
        this.localItem.attachments.push(name);
      } else {
        var index = this.localItem.attachments.indexOf(name);
        this.localItem.attachments.splice(index, 1);
      }
    },
    modelBackwardCompatibility(candidate) {
      var value = this.models.find(m => m.id == candidate);

      if (value) {
        return value.id;
      }

      for (const md of this.models) {
        if (!md.config) {
          continue;
        }
        var config = JSON.parse(md.config);
        if (config.deployment == candidate) {
          return md.id;
        }
      }

      return null;
    },
    getLabel(id) {
      return this.models.find(m => m.id === id)?.name ?? id;
    },
    onChange() {
      this.$emit('change', { ...this.localItem });
    },
    addMessage() {
      this.localItem.messages.push({
        role: this.localItem.messages.length % 2 === 1 ? 'user' : 'assistant',
        content: ''
      });
      this.onChange();
    },
    toggleAttach() {
      this.attachmentsExpanded = !this.attachmentsExpanded;
    },
    deleteMessage() {
      this.localItem.messages.pop();
      const lastMessage = this.localItem.messages[this.localItem.messages.length - 1];

      // Supplemental images are only allowed on user messages that are not the last message
      if (lastMessage.supplementals?.images?.length) {
        lastMessage.supplementals.images = [];
      }

      this.onChange();
    }
  }
};
</script>

<style scoped lang="scss">
.accordion-item {
  .accordion-item-header {
    display: grid;
    grid-template-columns: max-content 1fr max-content;
    align-items: center;
    align-content: center;
    min-height: 50px;
    height: 100%;
    background-color: var(--theme-surface);
    border-bottom: 1px solid var(--theme-on-surface);
    padding: 0 10px;

    .header-buttons {
      display: flex;

      .actions {
        display: flex;
        padding-right: 5px;
      }

      .ordering {
        display: grid;
        padding: 0 10px;
        grid-template-rows: 1fr 1fr;

        i {
          gap: 0;
          padding: 0;
          margin: 0;
          height: 18px;
          width: 18px;
          font-size: 18px;
        }

        i:first-child {
          justify-self: flex-start;
        }

        i:last-child {
          justify-self: flex-end;
        }
      }
    }
  }

  .title {
    margin-left: 10px;
  }

  .button {
    padding: 2px;
  }

  .active {
    cursor: pointer;
    :hover {
      background-color: var(--theme-on-surface);
      color: var(--theme-surface);
      border-radius: 2px;
    }
  }

  .accordion-item-content {
    padding: 10px;
    display: grid;
    gap: 10px;

    .settings {
      padding: 5px;
      gap: 10px;

      span {
        font-size: 0.75rem;
        font-style: italic;
      }

      .error {
        color: red;
      }

      .model {
        padding: 5px;
      }

      .params {
        display: flex;
        flex-wrap: wrap;
        list-style: none;
        padding: 5px;
        gap: 10px;

        li {
          width: 150px;
        }
      }

      .functions {
        grid-column: 1 / span 3;
        grid-template-rows: auto 1fr;
        display: grid;
        gap: 5px;
        padding-top: 5px;

        .list {
          background-color: var(--theme-surface);
          padding: 5px;
          .function {
            display: grid;
            grid-template-columns: 15px 1fr;
            gap: 10px;
            font-size: 0.85rem;
            padding: 5px;
            padding-left: 0;
            min-width: 150px;
            background-color: var(--theme-surface);

            .not-allowed {
              text-decoration: line-through;
              opacity: 0.6;
            }

            label {
              font-weight: bold;
              color: var(--theme-on-surface-accent);
            }
          }
        }
      }
    }

    .prompt-body-content {
      padding: 10px;

      .template-content {
        margin: 10px 0;
        display: grid;
        gap: 10px;

        .image-example {
          display: flex;
          gap: 10px;
          align-items: center;
          justify-items: stretch;
        }

        .label {
          font-weight: 500;
          font-size: 0.75rem;
          letter-spacing: 0.025em;
        }

        .editor {
          height: 200px;
        }

        .head {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
      }

      .buttons {
        display: flex;
        justify-content: space-between;
        align-items: center;
        justify-items: center;

        .attachments {
          position: relative;

          .attachemnts-pop-up {
            display: none;
            gap: 5px;
            padding: 10px;
            border: 1px solid white;
            background-color: var(--theme-surface);

            .attachment {
              display: flex;
              align-content: flex-start;
              gap: 5px;
              font-size: 0.85rem;
              padding: 5px;
              padding-left: 0;
              min-width: 150px;
            }
          }

          .expanded {
            display: flex;
            position: absolute;
            right: 0;
          }
        }
      }
    }
  }
}
</style>
